import { State } from '@meraki-internal/state';
import { CommunityClient } from './CommunityClient';
import { ICareMap } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import { WithLinks } from '../caremap/WithLinks';
import { CommunityModel } from './CommunityModel';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { Logger } from '../support/Logger';

export class CommunityModelFactory extends State<Record<string, never>> {
    static inject = () => [
        CareMapState,
        CommunityClient,
        OrganizationsState,
        BufferedEventBus,
        Logger
    ];

    constructor(
        private careMapState: CareMapState,
        public client: CommunityClient,
        public orgState: OrganizationsState,
        private events: BufferedEventBus,
        private logger: Logger
    ) {
        super({ });

        careMapState.subscribe(() => {
            const careMap = careMapState.getActiveCaremap();
            if (careMap && careMap.careMapId !== this.careMapId) {
                this.careMapId = careMap.careMapId;
                this.forActiveCaremap();
            }
        });

        this.events.on('web-sockets.message', this.onWebsocketMessage);
    };

    // Reload the access list of whichever cached model a realtime push targets, so the Sharing &
    // Access list and member access banners update live (e.g. when an invited viewer signs up and
    // becomes a collaborator) without a full page reload.
    private onWebsocketMessage = async (message?: IResourceChangedSocketMessage) => {
        if (!message) {
            return;
        }
        const model = Object.values(this.careMapIdToModel).find(m => m.matchesAccessListResource(message.resourceToRefresh));
        if (model) {
            try {
                await model.load();
            } catch (err: any) {
                // best-effort realtime refresh; the next load (reopen / page refresh) recovers.
                this.logger.error(`CommunityModelFactory.onWebsocketMessage reload failed: ${err?.toString?.() ?? err}`);
            }
        }
    };

    private careMapId?: string;
    private careMapIdToModel: { [careMapId: string]: CommunityModel } = {};

    forActiveCaremap = (): CommunityModel => {
        const activeCaremap = this.careMapState.getActiveCaremap() as WithLinks<ICareMap>;
        if (!activeCaremap) {
            // TODO: consider instead returning the most recent one
            // to avoid transition errors
            throw new Error('there is no active caremap');
        }
        if (!this.careMapIdToModel[activeCaremap.careMapId!]) {
            const model = new CommunityModel(activeCaremap, this, this.careMapState);

            // forActiveCaremap is likely called in a render
            // so we need to avoid doing anything sync that might change state
            // or react gets upset
            setTimeout(async () => {
                await model.load();

                // if the model changes, we change, that way components can just subscrive to the factory
                model.subscribe(() => {
                    this.setState({});
                });
            });


            this.careMapIdToModel[activeCaremap.careMapId!] = model;
        }
        return this.careMapIdToModel[activeCaremap.careMapId!];
    };
}
